import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCcw } from 'lucide-react'

interface Props {
  children?: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null })
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-md w-full bg-rose-500/10 border border-rose-500/20 rounded-[3rem] p-12 text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-rose-500/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="text-rose-500" size={40} />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black uppercase tracking-tight text-rose-500">Something went wrong</h2>
              <p className="text-sm font-bold text-rose-400 opacity-80 leading-relaxed">
                The dashboard encountered an unexpected error. This usually happens due to a temporary data sync issue.
              </p>
            </div>
            
            <div className="p-4 bg-slate-900/50 rounded-2xl text-left">
              <p className="text-[10px] font-mono text-rose-400/70 break-all overflow-hidden line-clamp-2">
                {this.state.error?.message}
              </p>
            </div>

            <button
              onClick={this.handleReset}
              className="w-full py-4 bg-rose-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-500 transition-all flex items-center justify-center gap-2"
            >
              <RefreshCcw size={14} />
              Reload Application
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
